<!-- 
Dialog showing our server-side app settings.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-card>
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>
          App settings
        </v-toolbar-title>
        <v-spacer/>
        <our-user-guide-button topic="administration-exception-logs" section="diagnostic-tools-app-settings"/>
      </v-toolbar>
      <our-loading-indicator v-if="loading"/>
      <v-tabs v-else color="accent" slider-color="primary" show-arrows>
        <v-tab key="environment" href="#">
          Environment
        </v-tab>
        <v-tab key="security" href="#">
          Security
        </v-tab>
        <v-tab key="testing" href="#">
          Testing
        </v-tab>
        <v-tab key="email" href="#">
          Email
        </v-tab>
        <v-tab key="databases" href="#">
          Databases
        </v-tab>
        <v-tab v-for="(configuration, index) in settings.sapConfigurations" :key="`sap-${index}`" href="#">
          SAP ({{ configuration.type }})
        </v-tab>
        <v-tab-item key="environment">
          <v-card-text>
            <our-data-container>
              <our-data-field label="Web root url" label-width="3">{{ settings.environmentWebRootUrl }}</our-data-field>
              <our-data-field label="Disclaimer url" label-width="3">{{ settings.environmentDisclaimerUrl }}</our-data-field>
              <our-data-field label="Copyright url" label-width="3">{{ settings.environmentCopyrightUrl }}</our-data-field>
              <our-data-field label="Privacy url" label-width="3">{{ settings.environmentPrivacyUrl }}</our-data-field>
              <our-data-field label="Client build" label-width="3">{{ settings.environmentClientBuild }}</our-data-field>
              <our-data-field label="Sortable list row limit" label-width="3">{{ settings.environmentSortableListRowLimit | number }}</our-data-field>
              <our-data-field label="Migrate database" label-width="3">{{ settings.environmentMigrateDatabase | yesNo }}</our-data-field>
              <our-data-field label="Use dummy SAP gateway" label-width="11">{{ settings.sapUseDummyGateway | yesNo }}</our-data-field>
            </our-data-container>
          </v-card-text>
        </v-tab-item>
        <v-tab-item key="security">
          <our-data-container>
            <our-data-field label="Days inactivity before locking account" label-width="3">{{ settings.securityDaysInactivityBeforeLockingAccount | number }}</our-data-field>
            <our-data-field label="JWT expiry (minutes)" label-width="3">{{ settings.securityJwtTokenExpiryInMinutes | number }}</our-data-field>
            <our-data-field label="Password encryption strength" label-width="3">{{ settings.securityPasswordEncryptionStrength | number }}</our-data-field>
            <our-data-field label="Password expiry period (days)" label-width="3">{{ settings.securityPasswordExpiryPeriodInDays | number }}</our-data-field>
          </our-data-container>
        </v-tab-item>
        <v-tab-item key="testing">
          <our-data-container>
            <our-data-field label="Enable EF profiler" label-width="3">{{ settings.testingEnableEfProfiler | yesNo }}</our-data-field>
            <our-data-field label="Enable testing tools" label-width="3">{{ settings.testingEnableTestingTools | yesNo }}</our-data-field>
          </our-data-container>
        </v-tab-item>
        <v-tab-item key="email">
          <our-data-container>
            <our-data-field label="Server" label-width="3">{{ settings.emailServer }}</our-data-field>
            <our-data-field label="Address override" label-width="3">{{ settings.emailAddressOverride }}</our-data-field>
            <our-data-field label="Disabled" label-width="3">{{ settings.emailDisabled | yesNo }}</our-data-field>
          </our-data-container>
        </v-tab-item>
        <v-tab-item key="databases">
          <our-data-container>
            <our-data-field label="LTPS" label-width="11">{{ settings.databaseConnectionString }}</our-data-field>
            <our-data-field label="HITS" label-width="11">{{ settings.databaseConnectionStringForHits }}</our-data-field>
            <our-data-field label="PRISMS" label-width="11">{{ settings.databaseConnectionStringForPrisms }}</our-data-field>
            <our-data-field label="Unistats" label-width="11">{{ settings.databaseConnectionStringForUnistats }}</our-data-field>
            <our-data-field label="VSL datamart" label-width="11">{{ settings.databaseConnectionStringForVslDatamart }}</our-data-field>
          </our-data-container>
        </v-tab-item>
        <v-tab-item v-for="(configuration, index) in settings.sapConfigurations" :key="`sap-${index}`">
          <our-data-container>
            <our-data-field label="Company code" label-width="3">{{ configuration.companyCode }}</our-data-field>
            <our-data-field label="General ledger account ID" label-width="3">{{ configuration.generalLedgerAccountId }}</our-data-field>
            <our-data-field label="Cost centre ID" label-width="3">{{ configuration.costCentreId }}</our-data-field>
            <our-data-field label="Sales tax code" label-width="3">{{ configuration.salesTaxCode }}</our-data-field>
            <our-data-field label="System client ID" label-width="3">{{ configuration.systemClientId }}</our-data-field>
            <our-data-field label="Certificate name" label-width="3">{{ configuration.certificateName }}</our-data-field>
            <our-data-field label="Use default web proxy" label-width="3">{{ configuration.useDefaultWebProxy | yesNo }}</our-data-field>
            <our-data-field label="Bypass proxy on local" label-width="3">{{ configuration.bypassProxyOnLocal | yesNo }}</our-data-field>
            <our-data-field label="Proxy credential type" label-width="3">{{ configuration.proxyCredentialType }}</our-data-field>
            <our-data-field label="Proxy address" label-width="11">{{ configuration.proxyAddress }}</our-data-field>
            <our-data-field label="Customer endpoint" label-width="11">{{ configuration.endpointForCustomer }}</our-data-field>
            <our-data-field label="Accounts receivable endpoint" label-width="11">{{ configuration.endpointForAccountsReceivable }}</our-data-field>
            <our-data-field label="Financial document extract endpoint" label-width="11">{{ configuration.endpointForFinancialDocumentExtract }}</our-data-field>
          </our-data-container>
        </v-tab-item>
      </v-tabs>
      <v-card-actions>
        <v-spacer/>
        <v-btn flat @click="hide">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as action from "@/store/system/action-types";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";

export default {
  mixins: [lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    state() {
      return this.$store.state.system.appSettings;
    },
    loaded() {
      return this.state.loaded;
    },
    loading() {
      return this.state.loading;
    },
    settings() {
      return this.state.data;
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        this.reload();
      }
    },
    loaded() {
      this.reload();
    }
  },
  methods: {
    reload() {
      const needsReloaded = !this.loaded && !this.loading;
      if (needsReloaded) {
        this.$store.dispatch(action.APP_SETTINGS_GET);
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
